import { useEffect, useState } from "react";
import { Loader2, MessageSquare, ExternalLink } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import type { EvidenceTimelineItem } from "@/components/coach/sessionPrep/EvidenceTimelineSection";
import { parseTranscriptAnalysisResult } from "@/lib/transcripts/parseTranscriptAnalysisResult";
import { invalidateTranscriptAnalysisQueries } from "@/lib/transcriptAnalysisInvalidation";
import { buildCoachPostMeetingRoute } from "@/lib/coachPostMeetingRoute";
import { useAuth } from "@/hooks/useAuth";
import { UnlinkedTranscriptBiNotice } from "@/components/coach/UnlinkedTranscriptBiNotice";

interface EvidenceDetailDialogProps {
  evidence: (EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null }) | null;
  onClose: () => void;
  onDone?: () => void;
  showDoneAction?: boolean;
  formatTimestamp: (value?: string | null) => string;
  onNavigate: (path: string) => void;
  linkLabel?: string | null;
  contextLine?: string | null;
}

// ─── Transcript Analysis Renderer ───

function renderFullAnalysis(result: unknown) {
  const parsed = parseTranscriptAnalysisResult(result);
  if (!parsed) {
    return <p className="text-muted-foreground text-sm">No analysis data available</p>;
  }

  const overallAssessment = parsed.overallAssessment;
  const strengths = parsed.strengths;
  const growthEdges = parsed.growthEdges;
  const keyInsight = parsed.keyInsight;
  const nextExperiment = parsed.nextExperiment;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Quality:</span>
        <Badge variant={parsed.presentation.badgeVariant}>
          {parsed.presentation.badgeLabel}
        </Badge>
      </div>
      {overallAssessment && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Overall</p>
          <p className="text-sm">{overallAssessment}</p>
        </div>
      )}
      {strengths && strengths.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">✓ Strengths</p>
          <div className="space-y-2">
            {strengths.map((s, i) => (
              <div key={i} className="pl-3 border-l-2 border-primary/40 text-sm">
                <p className="font-medium">{s.observation}</p>
                <p className="text-muted-foreground text-xs">{s.what_worked}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {growthEdges && growthEdges.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">↗ Growth Areas</p>
          <div className="space-y-2">
            {growthEdges.map((g, i) => (
              <div key={i} className="pl-3 border-l-2 border-accent text-sm">
                <p className="font-medium">{g.moment}</p>
                <p className="text-muted-foreground text-xs">Try: {g.micro_action}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {keyInsight && (
        <div className="bg-primary/5 p-3 rounded-lg">
          <p className="text-xs font-medium mb-1">Key Insight</p>
          <p className="text-sm">{keyInsight}</p>
        </div>
      )}
      {nextExperiment && (
        <div className="bg-accent/10 p-3 rounded-lg">
          <p className="text-xs font-medium text-accent-foreground mb-1">🧪 Next Experiment</p>
          <p className="text-sm">{nextExperiment}</p>
        </div>
      )}
    </div>
  );
}

// ─── Transcript Evidence Content ───

function TranscriptEvidenceContent({
  transcriptId,
  evidence,
  formatTimestamp,
  onNavigate,
  onClose,
}: {
  transcriptId: string;
  evidence: EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null };
  formatTimestamp: (value?: string | null) => string;
  onNavigate: (path: string) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [coachNote, setCoachNote] = useState("");

  const { data: transcript, isLoading, error: transcriptError } = useQuery({
    queryKey: ["transcript-evidence-detail", transcriptId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transcript_uploads")
        .select("id, leader_id, meeting_id, analysis_version, updated_at, analysis_result, analysis_status, coach_verified, coach_coaching_notes, summary_text")
        .eq("id", transcriptId)
        // maybeSingle returns null (not an error) when no row is found, preventing
        // an unhandled throw when the transcript ID is stale or inaccessible.
        .maybeSingle();
      if (error) {
        console.warn("transcript_evidence_query_failed");
        throw error;
      }
      return data as {
        id: string;
        leader_id: string;
        meeting_id: string | null;
        analysis_version: number | null;
        updated_at: string;
        analysis_result: unknown;
        analysis_status: string;
        coach_verified: boolean;
        coach_coaching_notes: string | null;
        summary_text: string | null;
      } | null;
    },
    retry: 1,
    enabled: Boolean(user?.id),
  });

  useEffect(() => {
    if (transcript?.coach_coaching_notes) {
      setCoachNote(transcript.coach_coaching_notes);
    }
  }, [transcript?.coach_coaching_notes]);

  const saveNoteMutation = useMutation({
    mutationFn: async (note: string) => {
      if (!transcript?.leader_id || !user?.id) {
        throw new Error("Transcript note context is no longer available.");
      }
      const expectedLeaderId = transcript.leader_id;
      const coachId = user.id;
      const { data, error } = await supabase
        .from("transcript_uploads")
        .update({
          coach_coaching_notes: note,
          coach_coaching_notes_submitted_at: new Date().toISOString(),
        })
        .eq("id", transcriptId)
        .eq("leader_id", expectedLeaderId)
        .select("id, leader_id")
        .maybeSingle();
      if (error) throw error;
      if (!data || data.id !== transcriptId || data.leader_id !== expectedLeaderId) {
        throw new Error("Transcript note save was not confirmed by the database.");
      }
      return {
        transcriptId: data.id,
        leaderId: data.leader_id,
        coachId,
      };
    },
    onSuccess: (result) => {
      void invalidateTranscriptAnalysisQueries({
        queryClient,
        transcriptId: result.transcriptId,
        leaderId: result.leaderId,
        coachId: result.coachId,
        proactiveMemoryReadback: "all",
      });
      toast.success("Transcript note saved.");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Unable to save transcript note.");
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const transcriptSourceLink = evidence.link || `/coach/post-meeting/${transcriptId}`;

  if (transcriptError) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Could not load this transcript. It may have been removed or may not be accessible.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => { onClose(); onNavigate(transcriptSourceLink); }}
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          Open in review page
        </Button>
      </div>
    );
  }

  if (!transcript) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          This transcript is no longer available in the index. It may have been re-uploaded or is still processing.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => { onClose(); onNavigate(transcriptSourceLink); }}
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          Open in review page
        </Button>
      </div>
    );
  }

  // Navigate to the post-meeting review page (the primary coach review workflow).
  const transcriptReviewLink = buildCoachPostMeetingRoute(transcriptId, {
    leaderId: transcript.leader_id,
    from: "leaders",
  });

  const isVerified = transcript.coach_verified === true;
  const isRejected = transcript.analysis_status === "rejected";
  const isPending = !isVerified && !isRejected;
  const hasAnalysis = !!transcript.analysis_result;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge variant="outline">{evidence.source}</Badge>
        {isVerified && <Badge className="bg-primary/10 text-primary border-primary/20">Verified</Badge>}
        {isRejected && <Badge variant="destructive">Rejected</Badge>}
        {isPending && hasAnalysis && <Badge variant="secondary">Awaiting Review</Badge>}
        <p className="text-xs text-muted-foreground">{formatTimestamp(evidence.created_at)}</p>
      </div>

      {hasAnalysis ? (
        <div className="rounded-lg border border-border/60 bg-muted/30 p-4 max-h-[40vh] overflow-y-auto">
          {renderFullAnalysis(transcript.analysis_result)}
        </div>
      ) : (
        <div className="rounded-lg border border-border/60 p-4">
          <p className="text-sm text-muted-foreground">
            {transcript.summary_text || evidence.detail || "No analysis available yet."}
          </p>
        </div>
      )}

      <UnlinkedTranscriptBiNotice phase="review" meetingId={transcript.meeting_id} analysisStatus={transcript.analysis_status} />

      {isPending ? (
        <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
          <p className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Transcript review saves happen in the review hub
          </p>
          <p className="text-sm text-muted-foreground">
            Use the review hub to save this transcript review, then reopen it later from this leader record.
          </p>
          <p className="text-xs text-muted-foreground">
            Saved reviews are added to the leader&apos;s coaching record and Coach Veera memory.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Private transcript note
          </label>
          <p className="text-xs text-muted-foreground">
            This note stays attached to this transcript. Use the review hub when the note should become part of the Agreement Record or Coach Veera memory.
          </p>
          <Textarea
            value={coachNote}
            onChange={(e) => setCoachNote(e.target.value)}
            placeholder="Add a private note about this transcript..."
            rows={3}
          />
          {coachNote !== (transcript.coach_coaching_notes || "") && (
            <Button type="button" size="sm" variant="outline" onClick={() => saveNoteMutation.mutate(coachNote)} disabled={saveNoteMutation.isPending}>
              {saveNoteMutation.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              Save note
            </Button>
          )}
        </div>
      )}

      {isPending && hasAnalysis && (
        <div className="space-y-3 pt-2 border-t border-border/60">
          <p className="text-sm text-muted-foreground">
            Save and close the transcript review from the review hub, then reopen the saved review later from this leader record.
          </p>
          <Button
            type="button"
            onClick={() => {
              onClose();
              onNavigate(transcriptReviewLink);
            }}
          >
            Save and close in review hub
          </Button>
        </div>
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-xs text-muted-foreground"
        onClick={() => { onClose(); onNavigate(transcriptReviewLink); }}
      >
        <ExternalLink className="h-3 w-3 mr-1" />
        Open in review page
      </Button>
    </div>
  );
}

// ─── Artifact Evidence Content ───

function ArtifactEvidenceContent({
  artifactId,
  evidence,
  formatTimestamp,
}: {
  artifactId: string;
  evidence: EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null };
  formatTimestamp: (value?: string | null) => string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["artifact-evidence-detail", artifactId],
    queryFn: async () => {
      const [artifactRes, extractionRes] = await Promise.all([
        supabase
          .from("leader_artifacts")
          .select("id, title, artifact_type, created_at, metadata")
          .eq("id", artifactId)
          .single(),
        supabase
          .from("leader_artifact_extractions")
          .select("id, extraction_json, created_at")
          .eq("artifact_id", artifactId)
          .maybeSingle(),
      ]);

      const artifact = artifactRes.data as { id: string; title: string | null; artifact_type: string; created_at: string; metadata: Record<string, unknown> } | null;
      const extraction = extractionRes.data as { id: string; extraction_json: Record<string, unknown>; created_at: string } | null;

      return { artifact, extraction };
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const artifact = data?.artifact;
  const extraction = data?.extraction;
  const extractionJson = (extraction?.extraction_json || {}) as Record<string, unknown>;
  const summary = typeof extractionJson.user_edited_summary === "string" && extractionJson.user_edited_summary.trim()
    ? extractionJson.user_edited_summary
    : typeof extractionJson.summary === "string"
      ? extractionJson.summary
      : null;
  const keyTraits = Array.isArray(extractionJson.key_traits)
    ? extractionJson.key_traits.filter((item): item is string => typeof item === "string")
    : [];
  const coachingFocus = Array.isArray(extractionJson.coaching_focus)
    ? extractionJson.coaching_focus.filter((item): item is string => typeof item === "string")
    : [];
  const feedback = extractionJson.user_feedback as string | null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline">{evidence.source}</Badge>
        {artifact?.artifact_type && <Badge variant="secondary">{artifact.artifact_type}</Badge>}
        {feedback && (
          <Badge className="bg-primary/10 text-primary border-primary/20">
            Leader {feedback === "agree" ? "agreed" : feedback === "disagree" ? "disagreed" : "edited"}
          </Badge>
        )}
        <p className="text-xs text-muted-foreground">{formatTimestamp(evidence.created_at)}</p>
      </div>

      {summary ? (
        <div className="rounded-lg border border-border/60 bg-muted/30 p-4 max-h-[40vh] overflow-y-auto">
          <p className="text-sm whitespace-pre-wrap">{summary}</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border/60 p-4">
          <p className="text-sm text-muted-foreground">
            {evidence.detail || "No extracted summary available yet."}
          </p>
        </div>
      )}

      {keyTraits.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Key Traits</p>
          <div className="flex flex-wrap gap-1.5">
            {keyTraits.map((trait, i) => (
              <Badge key={i} variant="outline" className="text-xs">{trait}</Badge>
            ))}
          </div>
        </div>
      )}

      {coachingFocus.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Coaching Focus Areas</p>
          <div className="flex flex-wrap gap-1.5">
            {coachingFocus.map((area, i) => (
              <Badge key={i} variant="secondary" className="text-xs">{area}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Reflection Evidence Content ───

function ReflectionEvidenceContent({
  evidence,
  formatTimestamp,
}: {
  evidence: EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null };
  formatTimestamp: (value?: string | null) => string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge variant="outline">{evidence.source}</Badge>
        <p className="text-xs text-muted-foreground">{formatTimestamp(evidence.created_at)}</p>
      </div>

      <div className="rounded-lg border border-border/60 bg-muted/30 p-4 max-h-[40vh] overflow-y-auto">
        <p className="text-sm whitespace-pre-wrap text-foreground">
          {evidence.detail || "No reflection content available."}
        </p>
      </div>
    </div>
  );
}

// ─── Main Dialog ───

export function EvidenceDetailDialog({
  evidence,
  onClose,
  onDone,
  showDoneAction = false,
  formatTimestamp,
  onNavigate,
  linkLabel,
  contextLine,
}: EvidenceDetailDialogProps) {
  if (!evidence) return null;

  const isTranscript = evidence.source === "Transcript" && evidence.subtype === "transcript";
  const transcriptId = isTranscript ? evidence.id.replace(/^transcript-/, "") : null;

  const isArtifact = evidence.source === "Artifact" && evidence.subtype === "artifact";
  const artifactId = isArtifact ? evidence.id.replace(/^artifact-/, "") : null;

  const isReflection = evidence.source === "Reflection";
  const isNotIndexed = evidence.availability === "not_indexed";

  const dialogDescription = isTranscript
    ? "Review transcript evidence and open the transcript review hub to save notes or a review decision."
    : isArtifact
      ? "Review the extracted artifact summary and leader feedback."
      : isReflection
        ? "Leader reflection content."
        : isNotIndexed
          ? "This source reference is not indexed in the current coaching workspace."
          : "Evidence summary with an optional link to the complete source.";

  return (
    <Dialog open={!!evidence} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{evidence.title || "Evidence"}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {isTranscript && transcriptId ? (
          <TranscriptEvidenceContent
            transcriptId={transcriptId}
            evidence={evidence}
            formatTimestamp={formatTimestamp}
            onNavigate={onNavigate}
            onClose={onClose}
          />
        ) : isArtifact && artifactId ? (
          <div className="space-y-4">
            <ArtifactEvidenceContent
              artifactId={artifactId}
              evidence={evidence}
              formatTimestamp={formatTimestamp}
            />
            {showDoneAction ? (
              <div className="pt-2 border-t border-border/60">
                <Button type="button" onClick={() => (onDone ?? onClose)()}>Done</Button>
              </div>
            ) : null}
          </div>
        ) : isReflection ? (
          <div className="space-y-4">
            <ReflectionEvidenceContent
              evidence={evidence}
              formatTimestamp={formatTimestamp}
            />
            {showDoneAction ? (
              <div className="pt-2 border-t border-border/60">
                <Button type="button" onClick={() => (onDone ?? onClose)()}>Done</Button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{evidence.source || "Evidence"}</Badge>
              {isNotIndexed ? <Badge variant="secondary">Not indexed</Badge> : null}
              <p className="text-xs text-muted-foreground">
                {isNotIndexed ? "Source date unavailable" : formatTimestamp(evidence.created_at)}
              </p>
            </div>
            <p className="text-sm whitespace-pre-wrap text-foreground">
              {evidence.detail || "No summary available yet."}
            </p>
            {contextLine && (
              <p className="text-xs text-muted-foreground">{contextLine}</p>
            )}
            {evidence.link && !isNotIndexed && (
              <div className="pt-2">
                <Button type="button" variant="outline" size="sm" onClick={() => { onClose(); onNavigate(evidence.link!); }}>
                  {linkLabel || "Open full source"}
                </Button>
              </div>
            )}
            {showDoneAction ? (
              <div className="pt-2 border-t border-border/60">
                <Button type="button" onClick={() => (onDone ?? onClose)()}>Done</Button>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
